import React, { useState, useEffect } from 'react';
import { Send, Bell, Settings2, AlertTriangle, Shield, ToggleLeft, ToggleRight, Megaphone, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../api';

const URGENCY_STYLES = {
  info:    { badge: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Info' },
  warning: { badge: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Warning' },
  critical:{ badge: 'bg-red-50 text-red-700 border-red-200', label: 'Critical' },
};

export default function SystemPage() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', urgency: 'info' });

  useEffect(() => {
    Promise.all([
      api.get('/master/platform/broadcasts').then(r => setBroadcasts(r.data || [])).catch(() => {}),
      api.get('/master/platform/settings').then(r => setSettings(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleBroadcast = async () => {
    if (!form.title || !form.message) { toast.error('Title and message are required'); return; }
    setSending(true);
    try {
      const res = await api.post('/master/platform/broadcast', form);
      toast.success(res.message || 'Broadcast sent!');
      setForm({ title: '', message: '', urgency: 'info' });
      const r = await api.get('/master/platform/broadcasts');
      setBroadcasts(r.data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await api.put('/master/platform/settings', settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingSettings(false); }
  };

  const updateSettings = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  return (
    <div className="p-6 lg:p-8 space-y-8 animate-fade-in" data-testid="system-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">System Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Platform-wide configuration and broadcast messaging</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Broadcast Section */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-[#516EFA]" />
              <h3 className="font-outfit font-semibold text-slate-900">Send Broadcast</h3>
              <p className="text-xs text-slate-400 ml-1">— to all active school admins</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Urgency Level</Label>
                <Select value={form.urgency} onValueChange={v => setForm(f => ({ ...f, urgency: v }))}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info — General announcement</SelectItem>
                    <SelectItem value="warning">Warning — Action needed</SelectItem>
                    <SelectItem value="critical">Critical — Urgent alert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Subject / Title</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. System Maintenance Notice" className="h-10" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Message</Label>
                <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Write your message to all school admins..." rows={4} className="text-sm" />
              </div>
              <Button onClick={handleBroadcast} disabled={sending || !form.title || !form.message} className="w-full bg-[#516EFA] hover:bg-[#4058e8] text-white" data-testid="send-broadcast-btn">
                {sending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  : <><Send className="w-4 h-4 mr-2" />Send to All Schools</>
                }
              </Button>
            </div>
          </div>

          {/* Recent Broadcasts */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-outfit font-semibold text-slate-900">Recent Broadcasts</h3>
              <Badge className="bg-slate-100 text-slate-500 text-xs">{broadcasts.length}</Badge>
            </div>
            {loading ? (
              <div className="p-4 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}</div>
            ) : broadcasts.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-400">No broadcasts sent yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
                {broadcasts.map((b, i) => {
                  const style = URGENCY_STYLES[b.urgency] || URGENCY_STYLES.info;
                  return (
                    <div key={i} className="px-6 py-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-semibold text-slate-800">{b.title}</p>
                        <Badge className={`text-[10px] border ${style.badge} flex-shrink-0`}>{style.label}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{b.message}</p>
                      <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
                        <Clock className="w-3 h-3" />
                        {b.sent_at ? new Date(b.sent_at).toLocaleDateString('en-NG') : '—'}
                        <span>·</span>
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        {b.sent_count || 0} schools
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Platform Settings */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-[#516EFA]" />
              <h3 className="font-outfit font-semibold text-slate-900">Platform Settings</h3>
            </div>
            {loading || !settings ? (
              <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}</div>
            ) : (
              <div className="p-6 space-y-5">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Support Email</Label>
                  <Input value={settings.support_email || ''} onChange={e => updateSettings('support_email', e.target.value)} placeholder="support@eduva.com" className="h-10" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Free Trial Days</Label>
                  <Input type="number" value={settings.trial_days || 30} onChange={e => updateSettings('trial_days', Number(e.target.value))} className="h-10" min={0} max={365} />
                  <p className="text-xs text-slate-400 mt-1">New schools get this many trial days before requiring a paid plan</p>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Grace Period (days)</Label>
                  <Input type="number" value={settings.grace_period_days ?? 30} onChange={e => updateSettings('grace_period_days', Number(e.target.value))} className="h-10" min={0} max={90} data-testid="grace-period-input" />
                  <p className="text-xs text-slate-400 mt-1">Days a school can still access the system after subscription expires before full lockout</p>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Max Schools on Platform</Label>
                  <Input type="number" value={settings.max_schools || 1000} onChange={e => updateSettings('max_schools', Number(e.target.value))} className="h-10" />
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full bg-[#516EFA] hover:bg-[#4058e8] text-white" data-testid="save-settings-btn">
                  {savingSettings ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Settings'}
                </Button>
              </div>
            )}
          </div>

          {/* Maintenance Mode */}
          <div className={`rounded-2xl border p-6 ${settings?.maintenance_mode ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-100'}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${settings?.maintenance_mode ? 'text-amber-600' : 'text-slate-400'}`} />
              <div className="flex-1">
                <p className="font-semibold text-slate-800">Maintenance Mode</p>
                <p className="text-xs text-slate-500 mt-1">When enabled, all school users see a maintenance screen and cannot log in.</p>
                <button
                  onClick={() => updateSettings('maintenance_mode', !settings?.maintenance_mode)}
                  className={`flex items-center gap-2 mt-4 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${settings?.maintenance_mode ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid="maintenance-toggle"
                >
                  {settings?.maintenance_mode
                    ? <><ToggleRight className="w-5 h-5 text-amber-600" />Maintenance ON — Click to disable</>
                    : <><ToggleLeft className="w-5 h-5 text-slate-500" />Maintenance OFF — Click to enable</>
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Platform Health */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-[#516EFA]" />
              <h3 className="font-outfit font-semibold text-slate-900 text-sm">Platform Health</h3>
            </div>
            <div className="space-y-3">
              {[
                { label: 'API Server', status: 'online' },
                { label: 'Database', status: 'online' },
                { label: 'Authentication', status: 'online' },
                { label: 'Email Service', status: 'check_config' },
              ].map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{s.label}</span>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${s.status === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                    <span className={`text-xs font-medium ${s.status === 'online' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {s.status === 'online' ? 'Online' : 'Check config'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
